import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, Drawer, IconButton, MenuItem, Paper,
    Popover, Table, TableBody, TableCell, TableContainer, TableHead,
    TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Layout from './Layout';
import PageHeader from './PageHeader';
import ConfirmModal from './modal/ConfirmModal';
import { useToast } from '../contexts/ToastContext';
import EmployeeService from '../services/EmployeeService';

const WorkspaceDrawer = ({ open, onClose, title, saveLabel, onSave, saving, children }) => (
    <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100vw', sm: 440 }, boxShadow: '-4px 0 32px rgba(15,23,42,0.12)' } }}
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(15,23,42,0.08)' } } }}
    >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ height: 3, bgcolor: '#427898', flexShrink: 0 }} />
            <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid #EEF2F6', flexShrink: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Typography fontWeight={700} fontSize={16} color="#0F172A">{title}</Typography>
                <IconButton size="small" onClick={onClose} sx={{ color: '#94A3B8', mt: -0.25 }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 3 }}>
                {children}
            </Box>
            <Box sx={{ px: 3, py: 2.5, flexShrink: 0, borderTop: '1px solid #EEF2F6', bgcolor: '#FAFBFC', display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                <Button onClick={onClose} sx={{ textTransform: 'none', color: '#64748B', fontWeight: 500 }}>Cancel</Button>
                <Button variant="contained" onClick={onSave} disabled={saving}
                    sx={{ bgcolor: '#427898', textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', px: 2.5, '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)' } }}>
                    {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : saveLabel}
                </Button>
            </Box>
        </Box>
    </Drawer>
);

const CONTRACT_TYPES = ['FULL_TIME', 'PART_TIME', 'CONTRACTOR', 'INTERN'];
const STATUS_OPTIONS = ['ACTIVE', 'ON_LEAVE', 'TERMINATED'];

const STATUS_COLOR = {
    ACTIVE:     { bg: '#DCFCE7', text: '#166534' },
    ON_LEAVE:   { bg: '#FEF9C3', text: '#854D0E' },
    TERMINATED: { bg: '#FEE2E2', text: '#991B1B' },
};

const ACCOUNT_COLOR = {
    NONE:    { bg: '#F1F5F9', text: '#64748B' },
    INVITED: { bg: '#FEF3C7', text: '#92400E' },
    ACTIVE:  { bg: '#DCFCE7', text: '#166534' },
};

const emptyForm = {
    firstName: '', lastName: '', email: '', phone: '',
    position: '', department: '', hireDate: '', contractType: '',
    status: 'ACTIVE', weeklyHours: '',
};

const EmployeesPage = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [employees, setEmployees] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [isCreateOpen, setCreateOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const [anchorEl, setAnchorEl] = useState(null);
    const [selected, setSelected] = useState(null);
    const [isDeleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const fetch = async () => {
        setLoading(true);
        try {
            const data = await EmployeeService.getEmployees(page, rowsPerPage);
            setEmployees(data.content || []);
            setTotal(data.totalElements || 0);
        } catch {
            showToast('Failed to load employees', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetch(); }, [page, rowsPerPage]); // eslint-disable-line

    const handleField = (f, v) => setForm(p => ({ ...p, [f]: v }));

    const validate = () => {
        const e = {};
        if (!form.firstName.trim()) e.firstName = 'First name is required';
        if (!form.lastName.trim())  e.lastName  = 'Last name is required';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleCreate = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const payload = {
                ...form,
                hireDate:     form.hireDate || null,
                weeklyHours:  form.weeklyHours || null,
                contractType: form.contractType || null,
            };
            const created = await EmployeeService.createEmployee(payload);
            setEmployees(p => [created, ...p]);
            setTotal(t => t + 1);
            setCreateOpen(false);
            setForm(emptyForm);
            showToast('Employee created', 'success');
        } catch {
            showToast('Failed to create employee', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await EmployeeService.deleteEmployee(selected.id);
            setEmployees(p => p.filter(e => e.id !== selected.id));
            setTotal(t => t - 1);
            setDeleteOpen(false);
            showToast('Employee deleted', 'warning');
        } catch {
            showToast('Failed to delete employee', 'error');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Layout>
            <Box sx={{ p: { xs: 2, md: 3 } }}>
                <PageHeader
                    title="Employees"
                    count={total}
                    action={
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => { setForm(emptyForm); setErrors({}); setCreateOpen(true); }}
                            sx={{ bgcolor: '#427898', borderRadius: '8px', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#355f77' } }}
                        >
                            Add employee
                        </Button>
                    }
                />

                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2 }}>
                    <Table>
                        <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#64748B' }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#64748B' }}>Position</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#64748B' }}>Department</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#64748B' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#64748B' }}>Account</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12, color: '#64748B' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                        <CircularProgress size={36} sx={{ color: '#427898' }} />
                                    </TableCell>
                                </TableRow>
                            ) : employees.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#94A3B8', fontSize: 13 }}>
                                        No employees yet
                                    </TableCell>
                                </TableRow>
                            ) : employees.map(emp => {
                                const sc = STATUS_COLOR[emp.status] || STATUS_COLOR.ACTIVE;
                                const ac = ACCOUNT_COLOR[emp.accountStatus] || ACCOUNT_COLOR.NONE;
                                return (
                                    <TableRow
                                        key={emp.id}
                                        hover
                                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#F8FAFC' } }}
                                        onClick={() => navigate(`/employees/${emp.id}`)}
                                    >
                                        <TableCell sx={{ fontWeight: 600, fontSize: 13, color: '#0F172A' }}>
                                            {emp.firstName} {emp.lastName}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 13, color: '#475569' }}>{emp.position || '—'}</TableCell>
                                        <TableCell sx={{ fontSize: 13, color: '#475569' }}>{emp.department || '—'}</TableCell>
                                        <TableCell>
                                            <Chip label={emp.status} size="small"
                                                sx={{ bgcolor: sc.bg, color: sc.text, fontWeight: 600, fontSize: 11, border: 'none' }} />
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={emp.accountStatus} size="small"
                                                sx={{ bgcolor: ac.bg, color: ac.text, fontWeight: 600, fontSize: 11, border: 'none' }} />
                                        </TableCell>
                                        <TableCell align="right" onClick={e => e.stopPropagation()}>
                                            <IconButton size="small" onClick={e => { setAnchorEl(e.currentTarget); setSelected(emp); }}>
                                                <MoreHorizIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={(_, p) => setPage(p)}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[5, 10, 25]}
                    />
                </TableContainer>

                {/* Row action menu */}
                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={() => { setAnchorEl(null); setSelected(null); }}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <MenuItem onClick={() => { navigate(`/employees/${selected?.id}`); setAnchorEl(null); }}>View</MenuItem>
                    <MenuItem onClick={() => { setDeleteOpen(true); setAnchorEl(null); }} sx={{ color: '#EF4444' }}>Delete</MenuItem>
                </Popover>

                {/* Create drawer */}
                <WorkspaceDrawer
                    open={isCreateOpen}
                    onClose={() => setCreateOpen(false)}
                    title="Add employee"
                    saveLabel="Create"
                    onSave={handleCreate}
                    saving={saving}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <TextField label="First name" fullWidth size="small" autoFocus value={form.firstName}
                                onChange={e => handleField('firstName', e.target.value)}
                                error={!!errors.firstName} helperText={errors.firstName || ' '} />
                            <TextField label="Last name" fullWidth size="small" value={form.lastName}
                                onChange={e => handleField('lastName', e.target.value)}
                                error={!!errors.lastName} helperText={errors.lastName || ' '} />
                        </Box>
                        <TextField label="Email" fullWidth size="small" type="email" value={form.email}
                            onChange={e => handleField('email', e.target.value)} />
                        <TextField label="Phone" fullWidth size="small" value={form.phone}
                            onChange={e => handleField('phone', e.target.value)} />
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <TextField label="Position" fullWidth size="small" value={form.position}
                                onChange={e => handleField('position', e.target.value)} />
                            <TextField label="Department" fullWidth size="small" value={form.department}
                                onChange={e => handleField('department', e.target.value)} />
                            <TextField label="Hire date" fullWidth size="small" type="date" InputLabelProps={{ shrink: true }}
                                value={form.hireDate} onChange={e => handleField('hireDate', e.target.value)} />
                            <TextField label="Contract type" fullWidth size="small" select value={form.contractType}
                                onChange={e => handleField('contractType', e.target.value)}>
                                <MenuItem value="">—</MenuItem>
                                {CONTRACT_TYPES.map(t => <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>)}
                            </TextField>
                            <TextField label="Status" fullWidth size="small" select value={form.status}
                                onChange={e => handleField('status', e.target.value)}>
                                {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>)}
                            </TextField>
                            <TextField label="Weekly hours" fullWidth size="small" type="number" value={form.weeklyHours}
                                onChange={e => handleField('weeklyHours', e.target.value)} />
                        </Box>
                    </Box>
                </WorkspaceDrawer>

                {/* Delete confirm */}
                <ConfirmModal
                    open={isDeleteOpen}
                    onClose={() => !deleting && setDeleteOpen(false)}
                    onConfirm={handleDelete}
                    question={`Delete employee "${selected?.firstName} ${selected?.lastName}"?`}
                    isLoading={deleting}
                />
            </Box>
        </Layout>
    );
};

export default EmployeesPage;
